import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import axios from 'axios'
import { formatDistanceToNowStrict } from 'date-fns'

const apiUrl = import.meta.env.VITE_API_URL

interface ApiKeyListItem {
  id: number
  name: string
  token_prefix: string
  created_by_auth0_id: string
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
}

interface ApiKeyCreateResponse extends ApiKeyListItem {
  token: string
}

interface ApiKeysDialogProps {
  open: boolean
  onClose: () => void
}

function extractErrorDetail(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const raw = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof raw === 'string' && raw.length > 0) return raw
    if (raw != null) return JSON.stringify(raw)
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function formatRelative(value: string | null): string {
  if (!value) return '—'
  try {
    return `${formatDistanceToNowStrict(new Date(value))} ago`
  } catch {
    return value
  }
}

function statusChip(key: ApiKeyListItem): React.ReactElement {
  if (key.revoked_at) {
    return <Chip size="small" color="default" label="Revoked" />
  }
  return <Chip size="small" color="success" label="Active" />
}

export const ApiKeysDialog: React.FC<ApiKeysDialogProps> = ({
  open,
  onClose,
}) => {
  const [keys, setKeys] = useState<ApiKeyListItem[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(
    null,
  )
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyListItem | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [revokeError, setRevokeError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ApiKeyListItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const fetchKeys = useCallback(async () => {
    setLoadError(null)
    try {
      const res = await axios.get<ApiKeyListItem[]>(`${apiUrl}/api-keys/`)
      setKeys(res.data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load'
      setLoadError(message)
      setKeys([])
    }
  }, [])

  useEffect(() => {
    if (open) {
      fetchKeys()
    } else {
      setKeys(null)
      setLoadError(null)
      setCreateOpen(false)
      setNewKeyName('')
      setCreateError(null)
      setCreatedKey(null)
      setCopyFeedback(null)
      setRevokeTarget(null)
      setRevokeError(null)
      setDeleteTarget(null)
      setDeleteError(null)
    }
  }, [open, fetchKeys])

  const sortedKeys = useMemo(() => {
    if (!keys) return []
    return [...keys].sort((a, b) => {
      const aActive = a.revoked_at ? 1 : 0
      const bActive = b.revoked_at ? 1 : 0
      if (aActive !== bActive) return aActive - bActive
      return b.created_at.localeCompare(a.created_at)
    })
  }, [keys])

  const handleOpenCreate = () => {
    setNewKeyName('')
    setCreateError(null)
    setCreateOpen(true)
  }

  const handleCreate = async () => {
    const name = newKeyName.trim()
    if (!name) {
      setCreateError('Name is required.')
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const res = await axios.post<ApiKeyCreateResponse>(
        `${apiUrl}/api-keys/`,
        { name },
      )
      setCreatedKey(res.data)
      setCreateOpen(false)
      await fetchKeys()
    } catch (err) {
      setCreateError(extractErrorDetail(err, 'Failed to create key'))
    } finally {
      setCreating(false)
    }
  }

  const handleCopyToken = async () => {
    if (!createdKey) return
    try {
      await navigator.clipboard.writeText(createdKey.token)
      setCopyFeedback('Copied to clipboard')
    } catch {
      setCopyFeedback('Copy failed — select and copy manually')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    setDeleteError(null)
    try {
      await axios.delete(`${apiUrl}/api-keys/${deleteTarget.id}`)
      setDeleteTarget(null)
      await fetchKeys()
    } catch (err) {
      setDeleteError(extractErrorDetail(err, 'Failed to delete key'))
    } finally {
      setDeleting(false)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    setRevoking(true)
    setRevokeError(null)
    try {
      await axios.post(`${apiUrl}/api-keys/${revokeTarget.id}/revoke`)
      setRevokeTarget(null)
      await fetchKeys()
    } catch (err) {
      setRevokeError(extractErrorDetail(err, 'Failed to revoke key'))
    } finally {
      setRevoking(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>API Keys</span>
          <Button variant="contained" size="small" onClick={handleOpenCreate}>
            New key
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            API keys authenticate the <code>fs</code> CLI. The plaintext token
            is shown <strong>once</strong> at creation — copy it before closing
            the dialog. Revoke immediately if a token is lost or leaked.
          </Typography>

          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {loadError}
            </Alert>
          )}

          {keys === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Prefix</TableCell>
                    <TableCell>Created by</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last used</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedKeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No API keys yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedKeys.map((key) => (
                      <TableRow key={key.id} hover>
                        <TableCell>{key.name}</TableCell>
                        <TableCell>
                          <code>{key.token_prefix}…</code>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={key.created_by_auth0_id}>
                            <Typography variant="body2" noWrap>
                              {key.created_by_auth0_id}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatRelative(key.created_at)}</TableCell>
                        <TableCell>
                          {formatRelative(key.last_used_at)}
                        </TableCell>
                        <TableCell>{statusChip(key)}</TableCell>
                        <TableCell align="right">
                          {key.revoked_at ? (
                            <Tooltip title="Delete permanently">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setDeleteError(null)
                                  setDeleteTarget(key)
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Revoke">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setRevokeError(null)
                                  setRevokeTarget(key)
                                }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create API key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Give the key a descriptive name (e.g. your laptop or a service).
            The plaintext token will be shown once after creation.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            disabled={creating}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleCreate()
              }
            }}
          />
          {createError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createdKey !== null}
        onClose={() => {
          setCreatedKey(null)
          setCopyFeedback(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>API key created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this token now — it will not be shown again.
          </Alert>
          {createdKey && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{createdKey.name}</strong>
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                <Box sx={{ flex: 1 }}>{createdKey.token}</Box>
                <Tooltip title="Copy">
                  <IconButton size="small" onClick={handleCopyToken}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {copyFeedback && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {copyFeedback}
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 2 }}>
                Save this in the CLI with:
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                }}
              >
                fs login --api-key {createdKey.token}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreatedKey(null)
              setCopyFeedback(null)
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revokeTarget !== null}
        onClose={() => !revoking && setRevokeTarget(null)}
      >
        <DialogTitle>Revoke API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoking <strong>{revokeTarget?.name}</strong> will immediately
            reject any further requests using this token. This cannot be
            undone.
          </DialogContentText>
          {revokeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRevoke}
            disabled={revoking}
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
      >
        <DialogTitle>Delete API key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently remove <strong>{deleteTarget?.name}</strong> from the
            database. Any audit trail entries linked to this key will keep
            their record but lose the key reference. This cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
